import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLWebWorkers = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">HTML5 APIs</div>
                <h1>Web Workers API</h1>
                <p className="hero-lead">
                    Run JavaScript in background threads with Web Workers. Keep your UI responsive
                    while performing heavy computations, processing large datasets, or handling complex calculations.
                </p>
            </div>

            <div className="content-section">
                <h2>What are Web Workers?</h2>
                <p>
                    Web Workers allow you to run JavaScript in background threads, separate from the main
                    UI thread. This prevents long-running scripts from freezing the page.
                </p>

                <div className="benefits-grid">
                    <div className="benefit-card">
                        <h3> Non-Blocking</h3>
                        <p>Heavy tasks don't freeze UI</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Performance</h3>
                        <p>Utilize multiple CPU cores</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Parallel Processing</h3>
                        <p>Run multiple workers simultaneously</p>
                    </div>
                    <div className="benefit-card">
                        <h3> Data Processing</h3>
                        <p>Perfect for large datasets</p>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Basic Web Worker Example</h2>

                <h3>Main Script (index.html)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Web Worker Demo</title>
</head>
<body>
    <button onclick="startWorker()">Start Worker</button>
    <button onclick="stopWorker()">Stop Worker</button>
    <div id="result"></div>

    <script>
    let worker;

    function startWorker() {
        // Create worker from external file
        worker = new Worker('worker.js');
        
        // Listen for messages from worker
        worker.onmessage = function(e) {
            document.getElementById('result').textContent = 
                'Worker says: ' + e.data;
        };
        
        // Handle errors
        worker.onerror = function(error) {
            console.error('Worker error:', error.message);
        };
        
        // Send message to worker
        worker.postMessage('Hello Worker!');
    }

    function stopWorker() {
        if (worker) {
            worker.terminate();
            worker = null;
        }
    }
    </script>
</body>
</html>`} />
                </div>

                <h3>Worker Script (worker.js)</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// worker.js - Runs in separate thread

// Listen for messages from main thread
self.onmessage = function(e) {
    console.log('Worker received:', e.data);
    
    // Do some work
    const result = 'Processed: ' + e.data;
    
    // Send result back to main thread
    self.postMessage(result);
};

// Or use addEventListener
self.addEventListener('message', function(e) {
    // Handle message
});`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Practical Example: Heavy Computation</h2>

                <h3>Main Script</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Prime Number Calculator</title>
</head>
<body>
    <h1>Prime Number Calculator (with Web Worker)</h1>
    <input type="number" id="max" value="100000" min="1000">
    <button onclick="calculatePrimes()">Calculate Primes</button>
    <button onclick="testUI()">Test UI (Click Me)</button>
    <div id="status">Ready</div>
    <div id="result"></div>

    <script>
    let worker;

    function calculatePrimes() {
        const max = document.getElementById('max').value;
        const status = document.getElementById('status');
        
        status.textContent = 'Calculating...';
        
        // Create worker
        worker = new Worker('prime-worker.js');
        
        worker.onmessage = function(e) {
            const primes = e.data;
            status.textContent = 'Done!';
            document.getElementById('result').textContent = 
                \`Found \${primes.length} prime numbers\`;
            worker.terminate();
        };
        
        // Send data to worker
        worker.postMessage({ max: parseInt(max) });
    }

    function testUI() {
        // This should work smoothly even during calculation
        alert('UI is responsive!');
    }
    </script>
</body>
</html>`} />
                </div>

                <h3>Worker Script (prime-worker.js)</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// prime-worker.js
self.onmessage = function(e) {
    const max = e.data.max;
    const primes = [];
    
    // Heavy computation (blocking in main thread, but OK in worker)
    for (let num = 2; num <= max; num++) {
        let isPrime = true;
        for (let i = 2; i <= Math.sqrt(num); i++) {
            if (num % i === 0) {
                isPrime = false;
                break;
            }
        }
        if (isPrime) {
            primes.push(num);
        }
        
        // Optional: Send progress updates
        if (num % 10000 === 0) {
            self.postMessage({ 
                type: 'progress', 
                current: num, 
                max: max 
            });
        }
    }
    
    // Send final result
    self.postMessage(primes);
};`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Inline Workers</h2>
                <p>Create workers without separate files using Blob URLs:</p>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Create worker code as string
const workerCode = \`
    self.onmessage = function(e) {
        const result = e.data * 2;
        self.postMessage(result);
    };
\`;

// Create blob from code
const blob = new Blob([workerCode], { type: 'application/javascript' });

// Create object URL
const workerUrl = URL.createObjectURL(blob);

// Create worker
const worker = new Worker(workerUrl);

worker.onmessage = function(e) {
    console.log('Result:', e.data);
};

worker.postMessage(10); // Result: 20

// Clean up when done
worker.terminate();
URL.revokeObjectURL(workerUrl);`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Transferring Data</h2>

                <h3>Structured Cloning (Default)</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Main thread
const data = {
    numbers: [1, 2, 3, 4, 5],
    text: 'Hello',
    nested: { value: 42 }
};

// Data is cloned (copied) to worker
worker.postMessage(data);

// Original data unchanged
console.log(data); // Still accessible`} />
                </div>

                <h3>Transferable Objects (Fast)</h3>
                <p>For large data (ArrayBuffers), transfer ownership instead of copying:</p>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Main thread
const buffer = new ArrayBuffer(1024 * 1024); // 1MB
const array = new Uint8Array(buffer);

// Fill with data
for (let i = 0; i < array.length; i++) {
    array[i] = i % 256;
}

// Transfer buffer (zero-copy, very fast)
worker.postMessage({ buffer: buffer }, [buffer]);

//  buffer is now transferred - can't use in main thread
console.log(buffer.byteLength); // 0 (detached)

// Worker receives the buffer
// worker.js
self.onmessage = function(e) {
    const buffer = e.data.buffer;
    console.log('Received:', buffer.byteLength); // 1048576
    
    // Process data...
    
    // Transfer back when done
    self.postMessage({ buffer: buffer }, [buffer]);
};`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Worker Scope and Limitations</h2>

                <h3>Available in Workers</h3>
                <ul>
                    <li><code>self</code> (global object)</li>
                    <li><code>navigator</code></li>
                    <li><code>location</code> (read-only)</li>
                    <li><code>XMLHttpRequest</code> / <code>fetch()</code></li>
                    <li><code>setTimeout()</code> / <code>setInterval()</code></li>
                    <li><code>importScripts()</code> (load external scripts)</li>
                    <li>Most JavaScript built-ins (Array, Object, Math, etc.)</li>
                </ul>

                <h3> NOT Available in Workers</h3>
                <ul>
                    <li><code>window</code></li>
                    <li><code>document</code></li>
                    <li><code>parent</code></li>
                    <li>DOM manipulation</li>
                    <li><code>localStorage</code> / <code>sessionStorage</code></li>
                    <li>Direct UI updates</li>
                </ul>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`// worker.js

// This works
self.postMessage('Hello');
fetch('https://api.example.com/data');
const array = [1, 2, 3];

//  This doesn't work
document.getElementById('result'); // Error: document is not defined
window.alert('Hi'); // Error: window is not defined
localStorage.setItem('key', 'value'); // Error: localStorage is not defined`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Importing Scripts in Workers</h2>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`// worker.js

// Import external scripts
importScripts('utils.js');
importScripts('math.js', 'helpers.js'); // Multiple files

// Now can use functions from imported scripts
self.onmessage = function(e) {
    // Use function from utils.js
    const result = processData(e.data);
    self.postMessage(result);
};`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Example: Image Processing</h2>

                <div className="code-example">
                    <CodeBlock language="html" code={`<!DOCTYPE html>
<html>
<head>
    <title>Image Processing with Worker</title>
</head>
<body>
    <input type="file" id="imageInput" accept="image/*">
    <canvas id="canvas"></canvas>
    <div id="status"></div>

    <script>
    const imageInput = document.getElementById('imageInput');
    const canvas = document.getElementById('canvas');
    const ctx = canvas.getContext('2d');
    const status = document.getElementById('status');

    imageInput.onchange = function(e) {
        const file = e.target.files[0];
        const reader = new FileReader();
        
        reader.onload = function(event) {
            const img = new Image();
            img.onload = function() {
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                
                // Get image data
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                
                // Process in worker
                status.textContent = 'Processing...';
                const worker = new Worker('image-worker.js');
                
                worker.onmessage = function(e) {
                    // Put processed image back
                    ctx.putImageData(e.data, 0, 0);
                    status.textContent = 'Done!';
                    worker.terminate();
                };
                
                // Send image data to worker
                worker.postMessage({ imageData: imageData });
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };
    </script>
</body>
</html>`} />
                </div>

                <h3>Worker (image-worker.js)</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`// image-worker.js
self.onmessage = function(e) {
    const imageData = e.data.imageData;
    const data = imageData.data;
    
    // Convert to grayscale
    for (let i = 0; i < data.length; i += 4) {
        const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
        data[i] = avg;     // Red
        data[i + 1] = avg; // Green
        data[i + 2] = avg; // Blue
        // data[i + 3] is alpha, leave unchanged
    }
    
    // Send processed image back
    self.postMessage(imageData);
};`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Shared Workers</h2>
                <p>
                    Shared Workers can be accessed by multiple scripts (tabs, iframes) from the same origin:
                </p>

                <div className="code-example">
                    <CodeBlock language="javascript" code={`// Main script (can be in multiple tabs)
const sharedWorker = new SharedWorker('shared-worker.js');

sharedWorker.port.onmessage = function(e) {
    console.log('Message from shared worker:', e.data);
};

sharedWorker.port.postMessage('Hello from tab');

// shared-worker.js
const connections = [];

self.onconnect = function(e) {
    const port = e.ports[0];
    connections.push(port);
    
    port.onmessage = function(e) {
        // Broadcast to all connected tabs
        connections.forEach(conn => {
            conn.postMessage('Message: ' + e.data);
        });
    };
    
    port.start();
};`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>

                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Good Use Cases</h3>
                        <ul>
                            <li>Heavy computations (math, crypto)</li>
                            <li>Large dataset processing</li>
                            <li>Image/video processing</li>
                            <li>Real-time data analysis</li>
                            <li>Background sync</li>
                            <li>Parsing large JSON/XML</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Poor Use Cases</h3>
                        <ul>
                            <li>Simple, quick operations</li>
                            <li>DOM manipulation</li>
                            <li>Operations needing window/document</li>
                            <li>Very frequent small messages (overhead)</li>
                        </ul>
                    </div>
                </div>

                <h3>Performance Tips</h3>
                <ul>
                    <li>Reuse workers instead of creating new ones</li>
                    <li>Use transferable objects for large data</li>
                    <li>Terminate workers when done</li>
                    <li>Send progress updates for long operations</li>
                    <li> Don't create too many workers (overhead)</li>
                    <li> Don't send UI objects to workers</li>
                </ul>
            </div>

            <div className="content-section">
                <h2>Browser Support</h2>
                <p>Web Workers are supported in all modern browsers:</p>
                <ul>
                    <li>Chrome 4+</li>
                    <li>Firefox 3.5+</li>
                    <li>Safari 4+</li>
                    <li>Edge (all versions)</li>
                    <li>iOS Safari 5+</li>
                    <li>Android 4.4+</li>
                </ul>

                <h3>Feature Detection</h3>
                <div className="code-example">
                    <CodeBlock language="javascript" code={`if (typeof Worker !== 'undefined') {
    // Web Workers supported
    const worker = new Worker('worker.js');
} else {
    // Fallback: run in main thread
    console.warn('Web Workers not supported');
}`} />
                </div>
            </div>

        </div>
    );
};

export default HTMLWebWorkers;



